/**
 * IndexedDB-backed {@link PackStore} (browser only).
 *
 * Implements the storage contract defined in `@lancer-vtt/compendium`
 * (`pack-store.ts`) against the browser's IndexedDB so user-loaded `.lcp` packs
 * survive a reload. The pure interface + its in-memory fake live in the
 * compendium package and are unit-tested headless there; THIS file is the real
 * browser glue.
 *
 * Store design:
 *   db   "lancer-vtt-compendium"  (version 1)
 *   store "packs"  keyPath: "id"  (the canonical pack id `<name>@<version>`)
 *   value: StoredPack { id, source?, bytes: Uint8Array, storedAt }
 *
 * `getAll` sorts by `storedAt` ascending so the merge order is stable
 * (oldest-first), preserving the compendium's later-pack-wins rule on rebuild.
 * Raw Uint8Array values are structured-cloneable, so the `.lcp` bytes persist
 * as-is and re-parse on reload via `rebuildCompendiumFromStore`.
 */
import type { PackStore, StoredPack } from "@lancer-vtt/compendium";
import { openDb, tx } from "./idb";

const DB_NAME = "lancer-vtt-compendium";
const DB_VERSION = 1;
const STORE = "packs";

function open(): Promise<IDBDatabase> {
  return openDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(STORE)) {
      db.createObjectStore(STORE, { keyPath: "id" });
    }
  });
}

/**
 * IndexedDB implementation of {@link PackStore}. One DB connection is opened
 * lazily and reused. Construct directly; pass into
 * `rebuildCompendiumFromStore(store)` on app start and `store.put(...)` when a
 * user loads a new `.lcp`.
 */
export class IdbPackStore implements PackStore {
  private dbPromise: Promise<IDBDatabase> | undefined;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) this.dbPromise = open();
    return this.dbPromise;
  }

  async put(pack: StoredPack): Promise<void> {
    const db = await this.db();
    await tx({ db, storeName: STORE, mode: "readwrite", fn: (store) => store.put(pack) });
  }

  async getAll(): Promise<StoredPack[]> {
    const db = await this.db();
    const all = (await tx<StoredPack[]>({ db, storeName: STORE, mode: "readonly", fn: (store) => store.getAll() })) ?? [];
    return all.sort((a, b) => a.storedAt - b.storedAt);
  }

  async delete(id: string): Promise<void> {
    const db = await this.db();
    await tx({ db, storeName: STORE, mode: "readwrite", fn: (store) => store.delete(id) });
  }

  async clear(): Promise<void> {
    const db = await this.db();
    await tx({ db, storeName: STORE, mode: "readwrite", fn: (store) => store.clear() });
  }
}

/** A process-wide singleton store, convenient for app wiring. */
export const packStore = new IdbPackStore();
